import type { Message} from './Communication';

import { sendMessage } from './Communication';

import { timeout } from '@egr/xbox/utils/Promise';

interface AuthServiceMessage<Action extends string, Arguments> extends Message<Arguments> {
  type: 'auth_service';
  action: Action;
}

export interface AuthData {
    /** indicator if the remaining login data is valid */
    valid: boolean;
    /** the current access_token for the user */
    access_token: string;
    /** number of seconds until access_token expires */
    expires_in: number;

}

/**
 * The APP should verify if the user is currently authenticated via pCon.login and return the last known access_token.
 * This method will first try to use an existing login and extend it via the locally stored refresh_token.
 * Failing to perform this it will present the user with a login mask to (re)authenticate with pCon.login.
 *
 * @param force If is set to true the user will be forced to perform a fresh login even if a valid refresh_token is present.
 */
export async function login(force: boolean = false): Promise<AuthData> {
    try {
        return await sendMessage<AuthServiceMessage<'login', {force: boolean}>, AuthData>({
            type: 'auth_service',
            action: 'login',
            arguments: {
                force
            }
        });
    } catch (error) {
        if (!error?.message.includes('invalid_grant')) {
            throw error;
        }

        logout();

        await timeout(50); // ugly hack to ensure the order of the two `postMessages` triggered in this block

        return await login(force);
    }
}

/** The APP should revoke the active access_token and invalidate the users login (if applicable). */
export function logout(): void {
    return sendMessage<AuthServiceMessage<'logout', undefined>>(
        {
            type: 'auth_service',
            action: 'logout',
            arguments: undefined
        },
        true
    );
}

/**
 * The APP should try to refresh an existing login and send the updated access_token to WEB.
 * If the refresh is not successful the user will be presented with a login mask.
 */
export function refresh(): Promise<AuthData> {
    return sendMessage<AuthServiceMessage<'refresh', undefined>, AuthData>({
        type: 'auth_service',
        action: 'refresh',
        arguments: undefined
    });
}

/** The APP should reply with the last known access_token. */
export function getAuthData(): Promise<AuthData> {
    return sendMessage<AuthServiceMessage<'get_data', undefined>, AuthData>({
        type: 'auth_service',
        action: 'get_data',
        arguments: undefined
    });
}